/**
 * /terceirizacao/pontualidade — Relatorio de pontualidade dos parceiros.
 *
 * Layout em 3 zonas:
 *   Zona 1: EntityHeader + Select de janela
 *   Zona 2: KpiStrip geral (total, no_prazo, atrasadas, % no prazo)
 *   Zona 3: Ranking dos parceiros (tabela ordenada por % no prazo desc)
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiArrowLeft, FiRefreshCw } from "react-icons/fi";

import EntityHeader from "../../../components/EntityHeader";
import KpiStrip from "../../../components/KpiStrip";
import { useAuth } from "../../../context/AuthContext";
import {
  CATEGORIA_LABELS,
  JANELAS_PONTUALIDADE,
  obterPontualidade,
} from "../../../services/terceirizacao";
import { PageContainer } from "../../../styles/shared";

import {
  Card,
  EmptyMsg,
  ErrorMsg,
  Pill,
  SectionTitle,
  Select,
  Table,
  TableWrapper,
} from "../../Orcamentos/styles";


function tonalidadePctNoPrazo(pct) {
  if (pct >= 90) return "success";
  if (pct >= 70) return "info";
  if (pct >= 50) return "warning";
  return "danger";
}


export default function PontualidadeRelatorio() {
  const navigate = useNavigate();
  const { request } = useAuth();

  const [janela, setJanela] = useState(90);
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const out = await obterPontualidade(request, { janela_dias: janela });
      setDados(out);
    } catch (err) {
      setError(err?.message || "Erro ao carregar pontualidade.");
      setDados(null);
    } finally {
      setLoading(false);
    }
  }, [request, janela]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const geral = dados?.geral || {
    total: 0, no_prazo: 0, atrasadas: 0,
    percentual_no_prazo: 0, atraso_medio_dias: 0,
  };
  const porParceiro = dados?.por_parceiro || [];

  const kpis = [
    {
      label: "OSEs finalizadas",
      value: geral.total,
      tone: "neutral",
      sub: `últimos ${janela}d`,
    },
    {
      label: "No prazo",
      value: geral.no_prazo,
      tone: geral.percentual_no_prazo >= 70 ? "success" : "warning",
      sub: `${geral.percentual_no_prazo}%`,
    },
    {
      label: "Atrasadas",
      value: geral.atrasadas,
      tone: geral.atrasadas > 0 ? "danger" : "success",
    },
    {
      label: "Atraso médio",
      value: `${geral.atraso_medio_dias}d`,
      tone: geral.atraso_medio_dias > 3 ? "danger" : "neutral",
    },
  ];

  return (
    <PageContainer>
      <EntityHeader
        title="Pontualidade dos parceiros"
        subtitle="Ranking de % no prazo + atraso médio (ADR-0025)"
        actions={[
          {
            label: "Voltar",
            icon: <FiArrowLeft size={14} />,
            onClick: () => navigate("/terceirizacao/oses"),
          },
          {
            label: "Atualizar",
            icon: <FiRefreshCw size={14} />,
            onClick: carregar,
          },
        ]}
      />

      <Card>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <label style={{ fontWeight: 600, fontSize: 14 }}>Janela:</label>
          <Select
            value={janela}
            onChange={(e) => setJanela(Number(e.target.value))}
            disabled={loading}
            style={{ minWidth: 200 }}
          >
            {JANELAS_PONTUALIDADE.map((j) => (
              <option key={j.value} value={j.value}>{j.label}</option>
            ))}
          </Select>
        </div>
      </Card>

      {error && <ErrorMsg>{error}</ErrorMsg>}

      <KpiStrip items={kpis} />

      <Card>
        <SectionTitle>Ranking dos parceiros</SectionTitle>
        {!dados ? (
          <EmptyMsg>{loading ? "Carregando..." : "Sem dados."}</EmptyMsg>
        ) : porParceiro.length === 0 ? (
          <EmptyMsg>
            Nenhuma OSE finalizada na janela. Aprove OSEs para ver o ranking.
          </EmptyMsg>
        ) : (
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <th style={{ width: 50 }}>#</th>
                  <th>Parceiro</th>
                  <th>Categoria</th>
                  <th style={{ textAlign: "right" }}>Total</th>
                  <th style={{ textAlign: "right" }}>No prazo</th>
                  <th style={{ textAlign: "right" }}>Atrasadas</th>
                  <th style={{ textAlign: "right" }}>% no prazo</th>
                  <th style={{ textAlign: "right" }}>Atraso médio</th>
                </tr>
              </thead>
              <tbody>
                {porParceiro.map((p, idx) => (
                  <tr key={p.parceiro_id}>
                    <td style={{ fontWeight: 600, color: idx === 0 ? "#4CAF50" : "inherit" }}>
                      {idx === 0 ? "🏆" : `#${idx + 1}`}
                    </td>
                    <td><strong>{p.parceiro_nome}</strong></td>
                    <td>{CATEGORIA_LABELS[p.categoria] || p.categoria}</td>
                    <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                      {p.total}
                    </td>
                    <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                      {p.no_prazo}
                    </td>
                    <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                      {p.atrasadas}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <Pill $tone={tonalidadePctNoPrazo(p.percentual_no_prazo)}>
                        {p.percentual_no_prazo}%
                      </Pill>
                    </td>
                    <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                      {p.atraso_medio_dias > 0
                        ? <span style={{ color: "var(--danger, #D32F2F)" }}>{p.atraso_medio_dias}d</span>
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>
        )}
      </Card>

      <Card>
        <div style={{ fontSize: 12, opacity: 0.7 }}>
          <strong>Como ler:</strong> Pontualidade calculada sobre OSEs finalizadas
          (APROVADA, REJEITADA ou RETORNADA) na janela selecionada. % no prazo &gt;= 90%
          = saudável; 70-89% = aceitável; 50-69% = atenção; &lt; 50% = crítico.
        </div>
      </Card>
    </PageContainer>
  );
}
